import { useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
import Checkbox from "components/Checkbox";
import CreditSimulator from "components/CreditSimulator";
import config, { DEFAULT_SCORE, MAX_REPAIRABLE_SCORE, MAX_SCORE } from "config";

import { Navigator, useStepper } from "../Stepper";
import Heading from "components/Heading";
import roundDownToNearest10 from "utils/roundDownToNearest10";
import CompactCreditSimulator from "components/CompactCreditSimulator";
import { colors } from "styles";
import WhiteBox from "components/WhiteBox";

export const simulateScore = (currentScore, selectedOptions = []) => {
  const maxBigImpactScore = 700;
  const maxBigImpactSelection = 3;

  if (currentScore >= maxBigImpactScore - maxBigImpactSelection * 10) {
    const newSimulatedScore = currentScore + selectedOptions.length * 10;
    const scoreLimit =
      currentScore >= 700
        ? currentScore > MAX_REPAIRABLE_SCORE
          ? currentScore
          : MAX_REPAIRABLE_SCORE
        : MAX_SCORE;

    return newSimulatedScore > scoreLimit ? scoreLimit : newSimulatedScore;
  }

  if (selectedOptions.length === config.recommendations.length) {
    return MAX_REPAIRABLE_SCORE;
  }

  const diffToMaxBigImpact = roundDownToNearest10(
    (maxBigImpactScore - currentScore) / maxBigImpactSelection
  );

  const diffSmallImpact = roundDownToNearest10(
    (MAX_REPAIRABLE_SCORE -
      (diffToMaxBigImpact * maxBigImpactSelection + currentScore)) /
      (config.recommendations.length - maxBigImpactSelection)
  );
  const sortedSelectedOptions = selectedOptions;
  const newSimulatedScore = sortedSelectedOptions.reduce((acc, _opt, index) => {
    return (
      acc +
      (index < maxBigImpactSelection ? diffToMaxBigImpact : diffSmallImpact)
    );
  }, currentScore);

  return newSimulatedScore > MAX_SCORE ? MAX_SCORE : newSimulatedScore;
};

const Recommendations = () => {
  const { getDefault } = useStepper();
  const currentScore = getDefault("currentScore") || DEFAULT_SCORE;
  const [selectedOptions, setSelectedOptions] = useState(
    getDefault("recommendations.selectedOptions") || []
  );
  const [simulatedScore, setSimulatedScore] = useState(currentScore);
  const [sticky, setSticky] = useState(false);
  const reason = getDefault("reason");
  const { firstName, lastName } = getDefault("personalInfo") || {};
  const simRef = useRef();
  // const storedRecommendations = getDefault("recommendations.selectedOptions");

  // useEffect(() => {
  //   setSelectedOptions([]);

  //   storedRecommendations.forEach((recommendation, index) => {
  //     setTimeout(() => {
  //       console.log(index, recommendation);
  //       setSelectedOptions((current) => [...current, recommendation]);
  //     }, (index + 1) * 250);
  //   });
  // }, [storedRecommendations]);

  useEffect(() => {
    setSimulatedScore(simulateScore(currentScore, selectedOptions));
  }, [currentScore, selectedOptions]);

  const listenToScroll = () => {
    const winScroll = document.documentElement.scrollTop;

    if (simRef.current) {
      const simHeight = simRef.current.clientHeight;
      const simMaxVisible = simHeight * 0.7;

      setSticky(winScroll - simMaxVisible > simMaxVisible);
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", listenToScroll);

    return () => window.removeEventListener("scroll", listenToScroll);
  }, []);

  return (
    <div>
      <div ref={simRef}>
        <CreditSimulator
          score={simulatedScore}
          loanType={reason}
          person={[firstName, lastName].filter(Boolean).join(" ")}
        />
      </div>
      <div
        style={{
          position: "absolute",
          width: "100%",
          height: "100%",
        }}
      >
        <ConditionStickyContainer sticky={sticky}>
          <CompactCreditSimulator
            score={simulatedScore}
            loanType={reason}
            person={[firstName, lastName].filter(Boolean).join(" ")}
          />
        </ConditionStickyContainer>
      </div>

      <div style={{ paddingTop: 30 }}>
        <Heading
          title={
            <>
              {/* Seleccione las recomendaciones y descubra cuanto{" "}
            <strong style={{ color: colors.blue }}>puede ahorrarse</strong> con
            un buen crédito */}
              Seleccione las recomendaciones y aplíquelas en su reparación de
              crédito
            </>
          }
        />
      </div>

      <ul style={{ paddingTop: 0 }}>
        {config.recommendations.map(({ id, title, body }) => (
          <li
            key={`option-${id}`}
            style={{
              padding: "5px 0",
            }}
          >
            <WhiteBox>
              <Checkbox
                id={`recommendation-${id}`}
                checked={selectedOptions.includes(id)}
                onChange={() => {
                  if (selectedOptions.includes(id)) {
                    setSelectedOptions(
                      selectedOptions.filter((op) => op !== id)
                    );
                  } else {
                    setSelectedOptions([...selectedOptions, id]);
                  }
                }}
              >
                {selectedOptions.includes(id) ? (
                  <strong>{title}</strong>
                ) : (
                  title
                )}
              </Checkbox>
            </WhiteBox>
          </li>
        ))}
      </ul>

      <Navigator
        saveDataHandler={() => ({ simulatedScore, selectedOptions })}
        $disableContinue={selectedOptions.length === 0}
        $layout="stack"
        $continueText="Reparar mi crédito"
      />
    </div>
  );
};

export default Recommendations;

const ConditionStickyContainer = styled.div`
  z-index: 10;
  margin: 0 -15px;
  top: 0;
  max-width: 600px;
  width: 100vw;
  height: auto;
  position: sticky;
  transition: all ease-in-out 250ms;

  ${(props) =>
    css`
      visibility: ${props.sticky ? "visible" : "hidden"};
      transform: translateY(${props.sticky ? "0px" : "-210px"});
    `}
`;

const Title = styled.strong`
  font-size: 1.2em;
`;
